/** @format */

import { BreakpointsService, UtilityService, WINDOW } from '@shared/data-access/services';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	ViewContainerRef,
	computed,
	effect,
	inject,
	input,
	signal
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HmfAccordionComponent, HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconListBullet
} from '@dsg-tech/homefield-angular-icons';
import {
	ProcessedNonBundleProduct,
	ProductSku,
	ProductStyle,
	SkuDescriptiveAttribute,
	StyleDescriptiveAttribute
} from '@shared/data-access/interfaces';

import { ActivatedRoute } from '@angular/router';
import { BreakPointNew } from '@shared/data-access/enums';
import { ImagesLoadedDirective } from '@shared/utils/directives';
import { MediaModule } from '@shared/media/components';
import { ProductService } from '@shared/data-access/product';
import { Store } from '@ngrx/store';
import { chain } from '@core/chain';
import { infoSummaryAttribute } from './info-summary-attributes.constants';
import { selectSustainabilityAttributes } from '@pdp/data-access/state';

type AttributeWithIcon = StyleDescriptiveAttribute & {
	icon?: string;
};

@Component({
	selector: 'pdp-product-description',
	templateUrl: './product-description.component.html',
	styleUrl: './product-description.component.scss',
	imports: [
		CommonModule,
		HmfAccordionComponent,
		HmfActionTextComponent,
		HomefieldIconLibraryModule,
		ImagesLoadedDirective,
		MediaModule
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDescriptionComponent implements OnInit {
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	/**
	 * @description
	 * Link to the non-PLA pdp page.
	 */
	public normalPDPLink: string;
	/**
	 * @description
	 * The current Product
	 */
	product = input<ProcessedNonBundleProduct | ProductStyle>();

	/**
	 * @description
	 * Resolved SKU ID
	 */
	resolvedSkuID = input<string>();

	/**
	 * @description
	 * Coming Soon End Date
	 */
	comingSoonEndDate = input<string>();

	/**
	 * @description
	 * Determines whether to show the sustainability icons and tooltips
	 */
	sustainablityIconsEnabled = input<boolean>();

	/**
	 * @description
	 * Determines whether to show the info summary
	 */
	infoSummaryEnabled = input(false);

	isOOSProductDescription = input(false);

	readonly allowAbbreviation = input<boolean>(true);

	private readonly platformId = inject(PLATFORM_ID);
	private readonly window = inject(WINDOW) as Window;
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly productService = inject(ProductService);
	private readonly route = inject(ActivatedRoute);
	private readonly utilityService = inject(UtilityService);
	private readonly store = inject(Store);
	private readonly breakpointsService = inject(BreakpointsService);

	/**
	 * @description
	 * Coming Soon boolean
	 */
	isComingSoon = computed(() =>
		this.comingSoonEndDate()
			? this.productService.isProductComingSoon(this.comingSoonEndDate())
			: undefined
	);

	isMobile = true;

	selectSustainabilityAttributes$ = this.store.select(selectSustainabilityAttributes);

	/**
	 * @description
	 * Flag for expanding/collapsing the description body accordion
	 */
	public readonly isAbbreviatedDescription = signal<boolean>(null);

	public isExpandable: boolean;

	/**
	 * @description
	 * Flag to display/hide the Pro Tips link
	 */
	public isProTipDisplayable = chain.proTipsFlag;

	/**
	 * @description
	 * California Proposition 65 config
	 */
	prop65ProductDisplayed = signal(false);
	prop65WarningText = signal('');

	/**
	 * @description
	 * ViewChild for description text
	 */
	@ViewChild('descriptionText', { read: ViewContainerRef, static: false })
	public descriptionText: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for displayable descriptive attributes
	 */
	@ViewChild('displayableAttributes', { read: ViewContainerRef, static: false })
	public displayableAttributes: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for Proposition 65 content
	 */
	@ViewChild('propSixtyFive', { read: ViewContainerRef, static: false })
	public propSixtyFive: ViewContainerRef;

	/**
	 * @description
	 * ViewChild for product style / SKU
	 */
	@ViewChild('productNumbers', { read: ViewContainerRef, static: false })
	public productNumbers: ViewContainerRef;

	/**
	 * @description
	 * Max Description Height for Read More button to be displayed
	 */
	readonly MAX_DESCRIPTION_HEIGHT = 171;

	/**
	 * @description
	 * Keeping track of inner witdh of screen so that we know if we should apply resize method
	 */
	private previousInnerWidth: number = null;

	/**
	 * @description
	 * List of attributes to display in the info summary box
	 */
	public infoSummary: AttributeWithIcon[] = [];

	/**
	 * @description
	 * Determines whether or not to show info summary.
	 */
	public shouldShowInfoSummary = false;

	/**
	 * @description
	 * Determines whether or not to show the icon in info summary.
	 */
	public displayInfoSummaryIcon = false;

	/**
	 * @description
	 * List of attributes to display in the specs under product information
	 */
	public displayableDescriptiveAttributes: StyleDescriptiveAttribute[] = [];

	public PRODUCT_ATTRIBUTE_IDENTIFIER = '2526';

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconListBullet]);

		if (isPlatformBrowser(this.platformId)) {
			effect(() => {
				const breakpoint = this.breakpointsService.breakpoint();
				const isMobileBreakpoint = breakpoint <= BreakPointNew.SMALL;
				this.onResize(isMobileBreakpoint);
			});
		}
	}

	ngOnInit(): void {
		this.setNormalPDPLink();
		this.initializeProp65Product(false);

		if (!this.product()) {
			return;
		}

		this.setDisplayableAttributes();
		this.checkForProp65Warnings();

		if (this.infoSummaryEnabled()) {
			this.handleInfoSummary();
		}

		this.changeDetectorRef.detectChanges();
	}

	public imagesLoaded(): void {
		if (!this.allowAbbreviation()) return;

		// Don't reset if athlete clicked "read more" before images finished loading
		if ((this.isAbbreviatedDescription() && this.isExpandable) || !this.isExpandable) {
			this.isAbbreviatedDescription.set(true);
		}
		this.calcDescriptionTextHeight();
	}

	public setDisplayableAttributes(): void {
		if (this.product().descriptiveAttributes) {
			this.displayableDescriptiveAttributes =
				this.productService.getDisplayableDescriptiveAttributesQV(
					this.product().descriptiveAttributes
				);
		}
	}

	public setNormalPDPLink(): void {
		const { seoToken, partNumber } = this.route.snapshot.params;
		this.normalPDPLink = `/p/${seoToken}/${partNumber}`;
	}

	public initializeProp65Product(isEnabled: boolean): void {
		this.prop65ProductDisplayed.set(isEnabled);
	}

	public checkForProp65Warnings(): void {
		const processedProd = this.product();
		if ('skus' in processedProd && Array.isArray(processedProd.skus)) {
			processedProd.skus.forEach((sku: ProductSku) => {
				this.checkProp65DescriptiveAttributes(sku.descriptiveAttributes);
			});
		}
	}

	public checkProp65DescriptiveAttributes(
		descriptiveAttributes: SkuDescriptiveAttribute[]
	): void {
		const prod65Message = descriptiveAttributes?.find(
			(attr) => attr.identifier === '4986'
		)?.value;
		if (typeof prod65Message === 'string') {
			this.initializeProp65Product(true);
			this.prop65WarningText.set(prod65Message);
		}
	}

	public handleInfoSummary(): void {
		const attributes = this.product().descriptiveAttributes || [];
		attributes.forEach((attribute) => {
			this.addToInfoSummaryCheck(attribute);
			this.showInfoSummaryCheck(attribute);
		});
	}

	/**
	 * @description
	 * Filters through the attr ids set in the constant file and compares to the ones on the product.
	 * It will push to a new array if the ids match so they can be used for display.
	 * This also appends an icon set in the constants file if the id matches the id property for the icon.
	 */

	addToInfoSummaryCheck(productAttribute: StyleDescriptiveAttribute) {
		const icons = {
			2564: 'PL-Tent-Capacity.svg',
			5576: 'PL-Tent-Weight.svg',
			1739: 'PL-Tent-Type.svg'
		};
		infoSummaryAttribute.ids.filter((attrIdsToAddToSummary: string) => {
			if (attrIdsToAddToSummary === productAttribute.identifier) {
				const addIcon = icons[attrIdsToAddToSummary];
				if (addIcon) {
					this.displayInfoSummaryIcon = true;
					productAttribute['icon'] = addIcon;
				}
				return this.infoSummary.push(productAttribute);
			}
		});
	}

	/**
	 * @description
	 * this filters through the values set in the constants infoSummaryAttribute object and  * compares the attr values on the current product page.
	 * This then sets the shouldShowInfoSummary value based on the condition.
	 */
	showInfoSummaryCheck(productAttribute: StyleDescriptiveAttribute) {
		infoSummaryAttribute.values.filter((attrValue: string) => {
			if (attrValue === productAttribute.value) {
				if (productAttribute.value === 'Mountain') {
					return (this.shouldShowInfoSummary =
						productAttribute.identifier === this.PRODUCT_ATTRIBUTE_IDENTIFIER);
				}
				return (this.shouldShowInfoSummary = true);
			}
		});
	}

	calcDescriptionTextHeight() {
		if (!isPlatformBrowser(this.platformId) || !this.allowAbbreviation()) {
			return;
		}

		//check that elements are in DOM before accessing the height
		if (
			!this.descriptionText?.element ||
			!this.displayableAttributes?.element ||
			!this.productNumbers?.element
		) {
			return;
		}

		const getDescriptionHeight = () =>
			this.utilityService.getElementHeight(
				this.descriptionText.element.nativeElement,
				true,
				true
			);
		const getAttributesHeight = () =>
			this.utilityService.getElementHeight(
				this.displayableAttributes.element.nativeElement,
				true,
				true
			);
		const getNumbersHeight = () =>
			this.utilityService.getElementHeight(
				this.productNumbers.element.nativeElement,
				true,
				true
			);
		const getPropSixtyFiveHeight = () =>
			this.propSixtyFive
				? this.utilityService.getElementHeight(
						this.propSixtyFive.element.nativeElement,
						true,
						true
					)
				: 0;

		const newDescriptionTextHeight = getDescriptionHeight();
		const newDisplayableAttributesHeight = getAttributesHeight();
		const newProductNumbersHeight = getNumbersHeight();
		const newPropSixtyFiveHeight = getPropSixtyFiveHeight();

		const combinedHeight =
			newDescriptionTextHeight +
			newDisplayableAttributesHeight +
			newProductNumbersHeight +
			newPropSixtyFiveHeight;

		this.isExpandable = combinedHeight >= this.MAX_DESCRIPTION_HEIGHT;

		if (!this.isExpandable) {
			this.isAbbreviatedDescription.set(false);
		}
	}

	public onResize(isMobileBreakpoint: boolean): void {
		if (!this.allowAbbreviation()) return;

		const width = this.window.innerWidth;
		if (this.previousInnerWidth === width) {
			// Return if there has been no change in screen width
			return;
		}

		// Set previous inner width
		this.previousInnerWidth = width;
		// Call to get the breakpoint only once, and only force recalculate on resize!
		this.isMobile = isMobileBreakpoint;

		this.changeDetectorRef.detectChanges();

		// this must be set before calcDescriptionTextHeight()
		this.isAbbreviatedDescription.set(true);

		this.calcDescriptionTextHeight();
	}

	trackByName(index: number, item: StyleDescriptiveAttribute): string {
		return index.toString() + item?.name;
	}

	trackByAttribute(index: number, item: StyleDescriptiveAttribute): string {
		return index.toString() + item?.name + item?.value;
	}

	handleAccordionChanges(active: boolean) {
		if (active && this.allowAbbreviation()) {
			this.isAbbreviatedDescription.set(true);
			this.calcDescriptionTextHeight();
		}
	}

	onReadMoreClicked() {
		this.isAbbreviatedDescription.set(false);
	}
}
